import React, { useEffect, useState } from 'react';
import { chequeService } from '../services/api';
import { Search, Banknote, Calendar, FileText, MessageSquare, X, Plus, Trash2, RefreshCw, Circle, Clock, User, DollarSign, XCircle, Filter } from 'lucide-react';

interface ChequeNota {
  id: number;
  cheque_id: number;
  nota: string;
  created_at: string;
  created_by_first_name: string;
  created_by_last_name: string;
}

interface Cheque {
  id: number;
  client_id: number;
  fecha_ck: string;
  plan: string;
  check_number: string;
  seguimiento_ck: string;
  factura: string;
  notas: string;
  facturador: string;
  facturado_desde: string;
  facturado_hasta: string;
  status: string;
  created_at: string;
  clientNotas?: ChequeNota[];
  facturas?: any[];
}

const statusConfig: Record<string, { bg: string; text: string; label: string; border: string; dot: string }> = {
  pending: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pendiente', border: 'border-amber-300', dot: '#F59E0B' },
  in_process: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'En Proceso', border: 'border-blue-300', dot: '#3B82F6' },
  completed: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Completado', border: 'border-emerald-300', dot: '#10B981' },
  denied: { bg: 'bg-red-100', text: 'text-red-700', label: 'Denegado', border: 'border-red-300', dot: '#EF4444' },
  paid: { bg: 'bg-violet-100', text: 'text-violet-700', label: 'Pagado', border: 'border-violet-300', dot: '#8B5CF6' },
};

const MyCheques: React.FC = () => {
  const [cheques, setCheques] = useState<Cheque[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState<'cheques' | 'paid' | 'denied'>('cheques');
  const [selectedCheque, setSelectedCheque] = useState<Cheque | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [newNota, setNewNota] = useState('');
  const [addingNota, setAddingNota] = useState(false);

  useEffect(() => {
    fetchCheques();
  }, []);

  const fetchCheques = async () => {
    setIsLoading(true);
    try {
      const response = await chequeService.getMyCheques();
      setCheques(response.data);
    } catch (error) {
      console.error('Error fetching cheques:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openChequeDetail = async (cheque: Cheque) => {
    try {
      const response = await chequeService.getById(cheque.id);
      setSelectedCheque(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching cheque details:', error);
    }
  };

  const closeDetail = () => {
    setShowDetailModal(false);
    setSelectedCheque(null);
    setNewNota('');
  };

  const handleAddNota = async () => {
    if (!newNota.trim() || !selectedCheque) return;
    setAddingNota(true);
    try {
      await chequeService.addNota(selectedCheque.id, { nota: newNota });
      const res = await chequeService.getById(selectedCheque.id);
      setSelectedCheque(res.data);
      setNewNota('');
    } catch (err) {
      console.error('Error adding nota:', err);
    } finally {
      setAddingNota(false);
    }
  };

  const filteredCheques = cheques.filter(cheque => {
    const matchSearch = !search || 
      cheque.check_number?.toLowerCase().includes(search.toLowerCase()) ||
      cheque.plan?.toLowerCase().includes(search.toLowerCase()) ||
      cheque.seguimiento_ck?.toLowerCase().includes(search.toLowerCase()) ||
      cheque.notas?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || cheque.status === statusFilter;
    return matchSearch && matchStatus;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const activeTabCheques = filteredCheques.filter(ck => {
    if (activeTab === 'cheques') return !['paid', 'denied'].includes(ck.status);
    if (activeTab === 'paid') return ck.status === 'paid';
    if (activeTab === 'denied') return ck.status === 'denied';
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Mis Cheques</h1>
          <p className="text-gray-500">Consulta el estado de tus cheques</p>
        </div>
        <button
          onClick={fetchCheques}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
        >
          <RefreshCw size={18} />
          Actualizar
        </button>
      </div>

      <div className="bg-white rounded-xl shadow">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('cheques')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition flex items-center justify-center gap-2 ${
                activeTab === 'cheques'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Banknote size={16} />
              Cheques ({cheques.filter(ck => !['paid', 'denied'].includes(ck.status)).length})
            </button>
            <button
              onClick={() => setActiveTab('paid')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition flex items-center justify-center gap-2 ${
                activeTab === 'paid'
                  ? 'border-violet-600 text-violet-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <DollarSign size={16} />
              Pagados ({cheques.filter(ck => ck.status === 'paid').length})
            </button>
            <button
              onClick={() => setActiveTab('denied')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition flex items-center justify-center gap-2 ${
                activeTab === 'denied'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <XCircle size={16} />
              Denegados ({cheques.filter(ck => ck.status === 'denied').length})
            </button>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por número, plan, seguimiento, notas..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendiente</option>
              <option value="in_process">En Proceso</option>
              <option value="completed">Completado</option>
              <option value="denied">Denegado</option>
              <option value="paid">Pagado</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cheque</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha CK</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Facturador</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notas</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {activeTabCheques.map((cheque) => {
                const st = statusConfig[cheque.status] || statusConfig.pending;
                return (
                  <tr key={cheque.id} onClick={() => openChequeDetail(cheque)} className="hover:bg-gray-50 transition cursor-pointer">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Circle size={10} fill={st.dot} stroke="none" />
                        <span className="font-medium text-gray-800">#{cheque.check_number || '-'}</span>
                        {cheque.seguimiento_ck && (
                          <span className="text-gray-400 text-xs">{cheque.seguimiento_ck}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full font-medium border ${st.bg} ${st.text} ${st.border}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{cheque.plan || '-'}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {cheque.fecha_ck ? new Date(cheque.fecha_ck).toLocaleDateString('en-US') : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                        {cheque.facturador || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs">
                      <div className="line-clamp-2 text-xs">{cheque.notas || '-'}</div>
                    </td>
                  </tr>
                );
              })}
              {activeTabCheques.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No se encontraron cheques
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-gray-200 text-sm text-gray-500">
          Mostrando {activeTabCheques.length} de {cheques.length} cheques
        </div>
      </div>

      {/* Cheque Detail Modal */}
      {showDetailModal && selectedCheque && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-gray-200/50">
            {/* Header */}
            <div className="relative px-8 py-6 bg-white border-b border-gray-100">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 bg-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-600/20">
                    <Banknote size={28} className="text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <h2 className="text-xl font-bold text-gray-900">
                        #{selectedCheque.check_number || selectedCheque.id}
                      </h2>
                      {(() => {
                        const st = statusConfig[selectedCheque.status] || statusConfig.pending;
                        return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>{st.label}</span>;
                      })()}
                    </div>
                    <p className="text-gray-500 mt-1">{selectedCheque.plan || 'Sin plan'}</p>
                  </div>
                </div>
                <button onClick={closeDetail} className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all">
                  <X size={22} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-100px)] p-8 bg-gray-50/50">
              {/* Key Info Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Banknote size={16} className="text-emerald-600" />
                    </div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</p>
                  </div>
                  <p className="text-base font-bold text-gray-900">{selectedCheque.plan || '-'}</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Calendar size={16} className="text-purple-600" />
                    </div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha CK</p>
                  </div>
                  <p className="text-base font-bold text-gray-900">{selectedCheque.fecha_ck ? new Date(selectedCheque.fecha_ck).toLocaleDateString('en-US') : '-'}</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Clock size={16} className="text-blue-600" />
                    </div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Seguimiento</p>
                  </div>
                  <p className="text-base font-bold text-gray-900">{selectedCheque.seguimiento_ck || '-'}</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                      <User size={16} className="text-orange-600" />
                    </div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Facturador</p>
                  </div>
                  <p className="text-base font-bold text-gray-900">{selectedCheque.facturador || '-'}</p>
                </div>
              </div>

              {/* Notas internas */}
              {selectedCheque.notas && (
                <div className="mb-8 p-5 bg-amber-50 border-l-4 border-amber-400 rounded-r-xl shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">!</span>
                    </div>
                    <h4 className="text-sm font-bold text-amber-800 uppercase tracking-wide">Notas</h4>
                  </div>
                  <p className="text-amber-900 leading-relaxed">{selectedCheque.notas}</p>
                </div>
              )}

              {/* Facturas asociadas */}
              {selectedCheque.facturas && selectedCheque.facturas.length > 0 && (
                <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <FileText size={18} className="text-blue-600" />
                      Facturas Asociadas
                    </h3>
                  </div>
                  <div className="p-5 space-y-3">
                    {selectedCheque.facturas.map((factura: any) => (
                      <div key={factura.id} className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-100">
                        <div>
                          <span className="font-mono font-bold text-blue-700">#{factura.factura_numero || 'N/A'}</span>
                          {factura.notas && <p className="text-sm text-gray-600 mt-1">{factura.notas}</p>}
                        </div>
                        <span className="text-xs text-gray-400">{factura.facturador || '-'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notas / Comunicación */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50/50">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <MessageSquare size={18} className="text-purple-600" />
                    Notas y Comunicación
                  </h3>
                </div>

                <div className="p-5">
                  {/* Add Note Form */}
                  <div className="mb-5 p-5 bg-purple-50/50 border border-purple-100 rounded-xl">
                    <textarea
                      value={newNota}
                      onChange={(e) => setNewNota(e.target.value)}
                      placeholder="Escribe una nota o mensaje sobre este cheque..."
                      className="w-full px-4 py-3 border border-purple-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none shadow-sm"
                      rows={3}
                    />
                    <div className="flex justify-end mt-3">
                      <button
                        onClick={handleAddNota}
                        disabled={!newNota.trim() || addingNota}
                        className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm"
                      >
                        {addingNota ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Plus size={16} />
                        )}
                        Agregar Nota
                      </button>
                    </div>
                  </div>

                  {/* Notes List */}
                  <div className="space-y-4 max-h-64 overflow-y-auto pr-2">
                    {selectedCheque.clientNotas && selectedCheque.clientNotas.length > 0 ? (
                      selectedCheque.clientNotas.map((nota) => (
                        <div key={nota.id} className="flex gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100/50 transition-colors">
                          <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                            <span className="text-white font-bold text-sm">
                              {nota.created_by_first_name ? nota.created_by_first_name.charAt(0).toUpperCase() : '?'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-semibold text-gray-900 text-sm">
                                {nota.created_by_first_name ? `${nota.created_by_first_name} ${nota.created_by_last_name}` : 'Usuario'}
                              </span>
                              <span className="text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full">
                                {new Date(nota.created_at).toLocaleDateString('en-US')}
                              </span>
                            </div>
                            <p className="text-gray-700 leading-relaxed text-sm">{nota.nota}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12 text-gray-400">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <MessageSquare size={28} className="text-gray-300" />
                        </div>
                        <p className="font-medium">Sin notas aún</p>
                        <p className="text-sm mt-1">¡Sé el primero en agregar una!</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCheques;
